import { countryDataType, DeviceType } from '@/src/types'
import { useEffect, useState, useCallback, useMemo } from 'react'
import {
    fetchItineraryData,
    getItineraryInfo,
    setContactDetails,
    setSelectedTravellerIndex,
    setTravellerData,
    setTravellerError,
    setValidationData
} from '@/src/store/slices/visaSlice/itineraryInfoSlice'
import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { getUserProfileData } from '@/src/store/slices/visaSlice/userProfileSlice'
import { usePathname } from 'next/navigation'
import debounce from 'lodash/debounce'
import { TravellerDetails } from '@/src/types/visa/visaItinerary'
import { validateTravellerInItinerary } from '@/src/utils/visa/travellers'
import { isEmpty } from '@/src/utils/helper'
import {
    createPrefilledTraveller,
    shouldPreFillTraveller,
    getPrefilledErrorFields,
    getPrefilledContactDetails,
    loadMobileCountryValidation
} from './prefillHelpers'

const useTravellerInfo = ({ deviceType }: { deviceType: DeviceType }) => {
    const dispatch = useAppDispatch()
    const pathname = usePathname()
    const { isLoading, data, metaData, error } =
        useAppSelector(getItineraryInfo)
    const userProfileData = useAppSelector(getUserProfileData)

    const segments = pathname.split('/')
    const itineraryId = segments[segments.length - 2]
    const { searchCriteria, customerDetails, totalFareComponent } = data || {}
    const {
        travellerDetails = [],
        isdCode,
        phoneNumber,
        customerEmail
    } = customerDetails || {}
    const { totalAmount, currencyCode } = totalFareComponent || {}
    const { paxInfo } = searchCriteria || {}
    const { adults, children } = paxInfo || {}
    const { selectedTravellerIndex, travellerValidations } = metaData || {}
    const showNextButton = selectedTravellerIndex < travellerDetails.length - 1

    const errorFields =
        (error as Record<number, Record<string, string>>)[
            selectedTravellerIndex
        ] || {}

    const [selectedTraveller, setSelectedTraveller] = useState(
        travellerDetails[selectedTravellerIndex]
    )

    // Track if pre-fill has been done
    const [prefillDone, setPrefillDone] = useState(false)
    // Create debounced update function (memoized)
    const updateTravellerInStore = useCallback(
        debounce((index: number, traveller: TravellerDetails) => {
            dispatch(
                setTravellerData({
                    index,
                    traveller
                })
            )
        }, 350),
        []
    )

    useEffect(() => {
        setSelectedTraveller(travellerDetails[selectedTravellerIndex])
    }, [selectedTravellerIndex, isLoading])

    useEffect(() => {
        // Always fetch fresh data for traveller page
        if (itineraryId) {
            dispatch(fetchItineraryData({ itineraryId, deviceType }))
        }
    }, [itineraryId, deviceType, dispatch])

    // Memoize prefill data computation - only for first traveller (index 0)
    const prefillData = useMemo(() => {
        if (
            !userProfileData ||
            travellerDetails.length === 0 ||
            selectedTravellerIndex !== 0 || // Only prefill first traveller
            prefillDone
        ) {
            return null
        }

        const firstTraveller = travellerDetails[0]

        if (!shouldPreFillTraveller(firstTraveller)) {
            return null
        }

        const prefilledTraveller = createPrefilledTraveller(
            userProfileData,
            firstTraveller
        )
        const clearedErrors = getPrefilledErrorFields(
            userProfileData,
            errorFields
        )
        const contactDetails = getPrefilledContactDetails(userProfileData, {
            customerEmail,
            phoneNumber,
            isdCode
        })

        return {
            traveller: prefilledTraveller,
            errors: clearedErrors,
            contact: contactDetails,
            needsValidation:
                !!contactDetails.isdCode && contactDetails.isdCode !== isdCode
        }
    }, [
        userProfileData,
        travellerDetails,
        selectedTravellerIndex,
        prefillDone,
        errorFields,
        customerEmail,
        phoneNumber,
        isdCode
    ])

    // Prefill handler - called when data is ready
    const handlePrefill = useCallback(async () => {
        if (!prefillData) return

        try {
            // Update traveller state and store
            setSelectedTraveller(prefillData.traveller)
            dispatch(
                setTravellerData({
                    index: selectedTravellerIndex,
                    traveller: prefillData.traveller
                })
            )

            // Clear validation errors
            dispatch(
                setTravellerError({
                    index: selectedTravellerIndex,
                    errorFields: prefillData.errors
                })
            )

            // Update contact details
            dispatch(setContactDetails(prefillData.contact))

            // Handle phone validation if needed
            if (prefillData.needsValidation && prefillData.contact.isdCode) {
                const validationData = await loadMobileCountryValidation(
                    prefillData.contact.isdCode
                )
                if (validationData) {
                    dispatch(setValidationData(validationData))
                }
            }

            setPrefillDone(true)
        } catch (error) {
            console.error('Prefill failed:', error)
            setPrefillDone(true) // Don't block user flow
        }
    }, [prefillData, selectedTravellerIndex, dispatch])

    // Trigger prefill when data is ready (only once)
    useEffect(() => {
        if (prefillData && !prefillDone) {
            handlePrefill()
        }
    }, [prefillData, prefillDone, handlePrefill])

    const personalDetailsFields = [
        'firstName',
        'lastName',
        'dateOfBirth',
        'gender'
    ]

    const contactDetailsFields = ['customerEmail', 'phoneNumber', 'isdCode']

    const onTravellerClick = (index: number) => {
        const contactDetails = { isdCode, phoneNumber, customerEmail }
        const { errorFields, isTravellerValid } = validateTravellerInItinerary(
            selectedTraveller,
            contactDetails,
            travellerValidations,
            searchCriteria,
            travellerDetails,
            selectedTravellerIndex
        )
        if (isTravellerValid) {
            dispatch(setSelectedTravellerIndex(index))
        } else {
            if (index < selectedTravellerIndex) {
                dispatch(setSelectedTravellerIndex(index))
            }
            dispatch(
                setTravellerError({
                    index: selectedTravellerIndex,
                    errorFields
                })
            )
        }
    }

    const onFieldChange = (field: string, value: string) => {
        const fieldToUpdate = personalDetailsFields.includes(field)
            ? 'personalDetails'
            : 'passportDetails'

        if (contactDetailsFields.includes(field)) {
            dispatch(setContactDetails({ [field]: value }))
        } else {
            const updatedTraveller = {
                ...selectedTraveller,
                [fieldToUpdate]: {
                    ...selectedTraveller[fieldToUpdate],
                    [field]: value
                }
            }
            setSelectedTraveller(updatedTraveller)
            updateTravellerInStore(selectedTravellerIndex, updatedTraveller)

            // Check for duplicate passport numbers in real-time
            if (field === 'passportNumber' && !isEmpty(value)) {
                const isDuplicate = travellerDetails.some(
                    (otherTraveller, index) => {
                        if (index === selectedTravellerIndex) return false
                        return (
                            otherTraveller.passportDetails.passportNumber?.toLowerCase() ===
                            value.toLowerCase()
                        )
                    }
                )

                if (isDuplicate) {
                    dispatch(
                        setTravellerError({
                            index: selectedTravellerIndex,
                            errorFields: {
                                ...errorFields,
                                passportNumber: 'Passport already used'
                            }
                        })
                    )
                    return
                }
            }
        }
        if (errorFields[field]) {
            dispatch(
                setTravellerError({
                    index: selectedTravellerIndex,
                    errorFields: {
                        ...errorFields,
                        [field]: ''
                    }
                })
            )
        }
    }

    const updateMobileValidation = (countryData: countryDataType) => {
        const { min, max, name } = countryData || {}
        dispatch(
            setValidationData({
                field: 'phoneNumber',
                validation: {
                    minLength: min,
                    maxLength: max,
                    selectedMobileCountry: name
                }
            })
        )
    }

    return {
        isLoading,
        adults,
        children,
        selectedTravellerIndex,
        selectedTraveller,
        travellerDetails,
        errorFields,
        isdCode,
        phoneNumber,
        customerEmail,
        showNextButton,
        totalAmount,
        currencyCode,
        onTravellerClick,
        onFieldChange,
        updateMobileValidation
    }
}

export default useTravellerInfo
